/**
 * 服务器存储服务
 * 
 * 负责项目数据与服务器的同步
 * 
 * 职责：
 * - 推送单个项目到服务器
 * - 推送所有项目到服务器
 * - 从服务器拉取项目
 */

import { v4 as uuidv4 } from 'uuid'
import type { Project } from '../../types/data'
import { pushProject as apiPushProject, getProject as apiGetProject } from '../api'
import { 
  uploadProjectMediaToServer, 
  replaceProjectMediaReferences,
  extractProjectMediaReferences
} from './mediaStoreService'
import { getAppSettings } from '../promptManagerDB'

// ============== 项目推送 ==============

export interface PushProjectResult {
  success: boolean
  error?: string
  mediaUploaded?: number
  mediaFailed?: number
}

/**
 * 推送单个项目到服务器（包含媒体文件上传）
 * 
 * 流程：
 * 1. 确保项目有 UUID
 * 2. 上传所有媒体文件到云端
 * 3. 替换项目中的媒体引用
 * 4. 推送项目 JSON 到服务器
 * 
 * @param userUuid 用户 UUID
 * @param project 项目数据
 * @param onProgress 进度回调
 * @returns 推送结果
 */
export async function pushProjectToServer(
  userUuid: string,
  project: Project,
  onProgress?: (stage: string, message: string) => void
): Promise<PushProjectResult> {
  try {
    if (!userUuid) {
      return { success: false, error: '用户未登录' }
    }
    
    // 确保项目有 UUID
    if (!project.uuid) {
      project.uuid = uuidv4()
    }
    
    // 确保所有 items 都有 UUID 和更新时间
    if (project.items) {
      project.items.forEach(item => {
        if (!item.uuid) {
          item.uuid = uuidv4()
        }
        item.updatedAt = new Date().toISOString()
      })
    }
    
    // 获取设置并添加到项目中
    try {
      let settings = await getAppSettings()
      if (!settings) {
        settings = {
          fontSize: 14,
          fontFamily: 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
          showBorder: true,
          mottoStyle: false,
          hideImageBorder: false
        }
      }
      
      const existingSettings = (project.settings as any) || {}
      project.settings = {
        fontSize: existingSettings.fontSize !== undefined ? existingSettings.fontSize : settings.fontSize,
        fontFamily: existingSettings.fontFamily !== undefined ? existingSettings.fontFamily : settings.fontFamily,
        showBorder: existingSettings.showBorder !== undefined ? existingSettings.showBorder : settings.showBorder,
        mottoStyle: existingSettings.mottoStyle !== undefined ? existingSettings.mottoStyle : (settings.mottoStyle || false),
        hideImageBorder: existingSettings.hideImageBorder !== undefined ? existingSettings.hideImageBorder : (settings.hideImageBorder || false),
        language: existingSettings.language || settings.language || 'zh-CN'
      }
    } catch (e) {
      console.warn('获取设置失败，使用默认值:', e)
    }
    
    // 更新项目的更新时间
    project.updatedAt = new Date().toISOString()
    
    // 提取媒体引用
    const mediaRefs = extractProjectMediaReferences(project)
    console.log(`📦 [pushProject] 提取到 ${mediaRefs.length} 个媒体文件引用`)
    
    // 创建项目数据副本用于上传
    const projectToUpload = JSON.parse(JSON.stringify(project)) as Project
    
    // 上传媒体文件
    let mediaUploaded = 0
    let mediaFailed = 0
    
    if (mediaRefs.length > 0) {
      onProgress?.('uploading_media', `正在上传 ${mediaRefs.length} 个媒体文件...`)
      
      const mediaResult = await uploadProjectMediaToServer(
        userUuid,
        project,
        (current, total, fileId) => {
          onProgress?.('uploading_media', `正在上传媒体文件 (${current}/${total}): ${fileId}`)
        }
      )
      
      mediaUploaded = mediaResult.success
      mediaFailed = mediaResult.failed
      
      console.log(`📦 [pushProject] 媒体上传完成: 成功 ${mediaUploaded}, 失败 ${mediaFailed}`)
      
      // 替换项目数据中的媒体引用
      if (mediaResult.mediaMapping.size > 0) {
        replaceProjectMediaReferences(projectToUpload, mediaResult.mediaMapping)
      }
    }
    
    // 推送项目 JSON 到服务器
    onProgress?.('pushing_project', `正在上传项目 "${project.name}"...`)
    
    await apiPushProject(userUuid, projectToUpload)
    
    console.log(`✅ [pushProject] 项目 "${project.name}" 上传成功`)
    
    return { 
      success: true, 
      mediaUploaded, 
      mediaFailed 
    }
  } catch (error) {
    console.error('推送项目失败:', error)
    return { 
      success: false, 
      error: (error as Error).message 
    }
  }
}

export interface PushAllProjectsResult {
  success: number
  failed: number
  errors: Array<{ projectName: string; error: string }>
}

/**
 * 推送所有项目到服务器
 * 
 * @param userUuid 用户 UUID
 * @param projects 项目列表
 * @param onProgress 进度回调
 * @returns 推送结果统计
 */
export async function pushAllProjectsToServer(
  userUuid: string,
  projects: Project[],
  onProgress?: (current: number, total: number, projectName: string) => void
): Promise<PushAllProjectsResult> {
  let success = 0
  let failed = 0
  const errors: Array<{ projectName: string; error: string }> = []
  
  for (let i = 0; i < projects.length; i++) {
    const project = projects[i]
    const projectName = project.name || '未命名画布'
    
    onProgress?.(i + 1, projects.length, projectName)
    
    try {
      const result = await pushProjectToServer(userUuid, project)
      
      if (result.success) {
        success++
      } else {
        failed++
        errors.push({ projectName, error: result.error || '未知错误' })
      }
    } catch (error) {
      failed++
      errors.push({ projectName, error: (error as Error).message })
    }
  }
  
  return { success, failed, errors }
}

// ============== 项目拉取 ==============

export interface PullProjectResult {
  success: boolean
  project?: Project
  error?: string
}

/**
 * 从服务器拉取单个项目
 * 
 * @param userUuid 用户 UUID
 * @param projectUuid 项目 UUID
 * @returns 拉取结果
 */
export async function pullProjectFromServer(
  userUuid: string,
  projectUuid: string
): Promise<PullProjectResult> {
  try {
    if (!userUuid) {
      return { success: false, error: '用户未登录' }
    }
    
    if (!projectUuid) {
      return { success: false, error: '项目 UUID 不存在' }
    }
    
    const serverProject = await apiGetProject(userUuid, projectUuid)
    
    if (!serverProject) {
      return { success: false, error: '云端没有该项目数据' }
    }
    
    return { success: true, project: serverProject }
  } catch (error) {
    console.error('拉取项目失败:', error)
    return { success: false, error: (error as Error).message }
  }
}
